import mapboxgl, { LngLat } from 'mapbox-gl';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AuthDialog } from 'components/AuthDialog/AuthDialog';
import { changeNewSpot, clearAddSpot } from 'store/slices/newSpot';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import Grid from '@mui/material/Unstable_Grid2';
import { Box, Button, Chip, Link, TextField, Typography } from '@mui/material';
import Loading from 'components/Loading';
import { styled } from 'theme';
import { Room, MyLocation } from '@mui/icons-material';
import { useDebounce } from 'react-use';
import { isCompleted, isNotFalsy, validate } from 'utils/Validation';
import { useAutoSaveDraft } from 'hooks/useAutoSaveDraft';

const PopupMapContainer = styled(Box)(({ theme }) => ({
    height: '180px',
    width: '100%',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
}));

const MarkerOverlay = styled(Box)({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -100%)',
    pointerEvents: 'none',
});

const AddSpotStep1 = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { save } = useAutoSaveDraft();
    const [center, setCenter] = useState<LngLat>({} as LngLat);
    const [zoomTmp, setZoomTmp] = useState<number>(NaN);
    const [debouncedCenter, setDebouncedCenter] = useState<LngLat>({} as LngLat);
    const [selectedPlace, setSelectedPlace] = useState<string | null>(null);
    const [customNameSelection, setCustomNameSelection] = useState<boolean>(false);
    const newSpot = useAppSelector((state) => state.newSpot);
    const {
        longitude,
        latitude,
        zoom,
        name,
        description,
        searchingSpots,
        searchingSpotsError,
        searchingSpotsSuccess,
        places,
    } = newSpot;

    const [errors, setErrors] = useState({ name: false, nameSelection: false });
    const [showErrors, setShowErrors] = useState(false);

    const validators = [
        {
            fieldName: 'name',
            fieldValue: name,
            isValid: (v: string) => !customNameSelection || isCompleted(v),
        },
        {
            fieldName: 'nameSelection',
            fieldValue: selectedPlace,
            isValid: (v: object) => customNameSelection || isNotFalsy(v),
        }
    ];

    const [,] = useDebounce(() => { setDebouncedCenter(center); }, 1000, [center]);

    useEffect(() => {
        if (debouncedCenter?.lat && debouncedCenter?.lng) {
            void dispatch(changeNewSpot({
                latitude: debouncedCenter.lat,
                longitude: debouncedCenter.lng,
                zoom: zoomTmp,
            }));
        }
    }, [debouncedCenter]);

    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const nameFieldRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (customNameSelection) nameFieldRef.current?.focus();
    }, [customNameSelection]);

    useEffect(() => {
        if (latitude && longitude && mapContainerRef.current) {
            const map = new mapboxgl.Map({
                container: mapContainerRef.current,
                style: 'mapbox://styles/mapbox/outdoors-v12',
                center: [longitude, latitude],
                zoom: zoom > 0 ? zoom : 16.5,
                attributionControl: false,
            });
            map.on('moveend', () => { setCenter(map.getCenter() as LngLat); setZoomTmp(map.getZoom()); });
            map.on('zoomend', () => { setCenter(map.getCenter() as LngLat); setZoomTmp(map.getZoom()); });
        }
    }, [latitude, longitude, mapContainerRef.current]);

    useEffect(() => {
        if (searchingSpotsError) history.push('/add-spot/error');
    }, [searchingSpotsError]);

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        void dispatch(changeNewSpot({ name: event.target.value }));
    };

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        void dispatch(changeNewSpot({ description: event.target.value }));
    };

    const filteredPlaces = places?.filter(
        (p) => !p?.place_type?.includes('country') &&
               !p?.place_type?.includes('region') &&
               !p?.place_type?.includes('postcode')
    );

    const hasPlaces = searchingSpotsSuccess && filteredPlaces?.length > 0;

    if (!customNameSelection && searchingSpotsSuccess && !hasPlaces) {
        setCustomNameSelection(true);
        setSelectedPlace(null);
        void dispatch(changeNewSpot({ name: '' }));
    }

    return (
        <AuthDialog
            onClose={() => { save(1); dispatch(clearAddSpot()); history.push('/'); }}
            title={'Create a New Spot'}
        >
            <form>
                <Grid container spacing={1}>
                    {/* Map */}
                    <Grid xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                            <MyLocation sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                Drag the map to place the pin exactly on your spot
                            </Typography>
                        </Box>
                        <Box sx={{ position: 'relative' }}>
                            <PopupMapContainer ref={mapContainerRef} />
                            <MarkerOverlay>
                                <Room sx={{ fontSize: 40, color: '#e53935', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))' }} />
                            </MarkerOverlay>
                        </Box>
                    </Grid>

                    {/* Coordinates */}
                    {!!(latitude && longitude) && (
                        <Grid xs={12}>
                            <Box sx={{ display: 'flex', gap: 2, px: 0.5 }}>
                                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                    <strong>Lat</strong> {latitude.toFixed(6)}
                                </Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                    <strong>Lng</strong> {longitude.toFixed(6)}
                                </Typography>
                            </Box>
                        </Grid>
                    )}

                    {/* Name selection */}
                    <Grid xs={12}>
                        {!customNameSelection ? (
                            searchingSpots ? (
                                <Loading text={'Finding nearby places...'} />
                            ) : hasPlaces ? (
                                <Box>
                                    <Typography variant='body2' color='text.secondary' mt={2} mb={1}>
                                        Pick a name or enter your own:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                        {filteredPlaces.map((p) => (
                                            <Chip
                                                key={p.place_name}
                                                label={p.place_name}
                                                onClick={() => {
                                                    setSelectedPlace(p.place_name);
                                                    void dispatch(changeNewSpot({ name: p.place_name }));
                                                }}
                                                color={selectedPlace === p.place_name ? 'primary' : 'default'}
                                                variant={selectedPlace === p.place_name ? 'filled' : 'outlined'}
                                                sx={{ 'maxWidth': '100%', 'height': 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                                            />
                                        ))}
                                    </Box>
                                    {showErrors && errors.nameSelection && (
                                        <Typography color='error' sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                                            Please choose a name for this spot
                                        </Typography>
                                    )}
                                    <Typography sx={{ fontSize: '0.8rem', mt: 1 }}>
                                        <Link color='secondary' onClick={() => {
                                            setCustomNameSelection(true);
                                            void dispatch(changeNewSpot({ name: '' }));
                                        }} underline='hover' sx={{ cursor: 'pointer' }}
                                        >
                                            Enter a custom name instead
                                        </Link>
                                    </Typography>
                                </Box>
                            ) : null
                        ) : (
                            <Box>
                                <TextField
                                    fullWidth
                                    label='Spot name'
                                    size='small'
                                    type='text'
                                    value={name}
                                    onChange={handleNameChange}
                                    placeholder='Give this spot a unique name'
                                    inputRef={nameFieldRef}
                                    error={showErrors && errors.name}
                                />
                                {showErrors && errors.name && (
                                    <Typography color='error' sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                                        Please enter a name for this spot
                                    </Typography>
                                )}
                                {hasPlaces && (
                                    <Typography sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                                        <Link color='secondary' onClick={() => {
                                            setCustomNameSelection(false);
                                            setSelectedPlace(null);
                                            void dispatch(changeNewSpot({ name: '' }));
                                        }} underline='hover' sx={{ cursor: 'pointer' }}
                                        >
                                            See suggested names
                                        </Link>
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Grid>

                    {/* Description */}
                    <Grid xs={12}>
                        <TextField
                            fullWidth
                            label='Description (optional)'
                            multiline
                            rows={2}
                            size='small'
                            value={description}
                            onChange={handleDescriptionChange}
                            placeholder='What makes this spot special?'
                        />
                    </Grid>
                </Grid>

                <Box sx={{ textAlign: 'center', pt: 2 }}>
                    <Button
                        variant='contained'
                        color='primary'
                        onClick={() => {
                            if (validate(validators, setErrors)) {
                                save(1);
                                history.push('/add-spot/step-2');
                            } else {
                                setShowErrors(true);
                            }
                        }}
                    >
                        Continue
                    </Button>
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 1, fontSize: '.85em' }}>
                        Next: categories &amp; hazards
                    </Typography>
                </Box>
            </form>
        </AuthDialog>
    );
};

export default AddSpotStep1;
